import type { TableWidgetModel, TableWidgetRuntimeState } from '.';
import type { WidgetHeaderElement } from '../WidgetModel';
import { makeHeaderElements } from '../WidgetModel';
import { COMMON_HEADER_ELEMENTS } from '../WidgetModel/impl';
import type { TableWidgetState } from './TableSelfOwnedModel';
import { TableSelfOwnedModel, tableWidgetStateSchema } from './TableSelfOwnedModel';
import { effect, untracked } from '@preact/signals-react';
import type { ReadonlySignal } from '@preact/signals-react';
import type { OutputColumnWithMetadata } from '@thinkalpha/platform-ws-client/contracts/table.js';
import { getIdleTableThrottle } from '@thinkalpha/ui-core/getIdleTableThrottle';
import type { Logger } from '@thinkalpha/ui-ioc/core/types/Logger';
import type { GridRowSelectionChange } from '@thinkalpha/ui-table-svelte/gridViewProps';
import type { GridRowId } from '@thinkalpha/ui-table/types/row';
import type { ColumnTemplate } from '@thinkalpha/ui-types/contracts/column-template';
import type { TableClass, TableWidgetViewData } from '@thinkalpha/ui-types/contracts/workspace';
import { inject, injectable } from 'src/features/ioc';
import { enableTableColumnFilters } from 'src/lib/featureFlags/flags/enableTableColumnFilters';
import type { ColumnFiltersModel } from 'src/models/ColumnFiltersModel';
import type { TableColumnFilters } from 'src/models/ColumnFiltersModel/tableColumnFilters';
import type { TableModel } from 'src/models/TableModel';
import type { ReactWidgetContainer } from 'src/ReactContainer';
import type { PageVisibilityService } from 'src/services/PageVisibilityService';
import type { ProxyClientService } from 'src/services/ProxyClientService';
import type { WorkspaceService } from 'src/services/WorkspaceService';
import { decodeSelfOwnedWidgetState } from 'src/services/WorkspaceService/selfOwnedWidget';
import type { GetInitialData, WidgetId } from 'src/services/WorkspaceService/selfOwnedWidget';
import type { WidgetContainerController } from 'src/services/WorkspaceService/WidgetContainer/WidgetContainer';
import type { ReactBindings } from 'src/types/bindings';

@injectable()
export class TableWidgetModelImpl implements TableWidgetModel {
    static readonly stateSchema = tableWidgetStateSchema;

    static defaultState(): TableWidgetState {
        return { table: null, class: null, filter: null, viewData: null };
    }

    static fromJSON(container: ReactWidgetContainer): TableWidgetModelImpl {
        const getInitialData = container.get('GetInitialData') as GetInitialData<unknown>;
        const raw = getInitialData();
        const validated = decodeSelfOwnedWidgetState(TableWidgetModelImpl.stateSchema, raw);
        container.bind('InitialWidgetState').toConstantValue(validated);
        return container.get('TableWidgetModel') as TableWidgetModelImpl;
    }

    readonly #model: TableSelfOwnedModel;
    readonly #table: TableModel;
    readonly #disposableStack = new DisposableStack();
    readonly headerElements: WidgetHeaderElement[];

    constructor(
        @inject('WidgetId') tabId: WidgetId,
        @inject('WidgetContainerController') container: WidgetContainerController,
        @inject('InitialWidgetState') initialState: TableWidgetState,
        @inject('Logger') logger: Logger,
        @inject('TableModel') table: TableModel,
        @inject('ProxyClientService') proxyClientService: ProxyClientService,
        @inject('WorkspaceService') workspaceService: WorkspaceService,
        @inject('PageVisibilityService') pageVisibility: PageVisibilityService,
        @inject('FeatureFlagService') featureFlagService: Pick<ReactBindings['FeatureFlagService'], 'getSignal'>,
    ) {
        this.#table = this.#disposableStack.use(table);
        this.#model = this.#disposableStack.use(
            new TableSelfOwnedModel({
                container,
                tabId,
                persisted: initialState,
                logger,
                proxyClientService,
                table: this.#table,
                workspaceService,
                columnFiltersEnabled: featureFlagService.getSignal(enableTableColumnFilters),
            }),
        );

        if (!APP_ELECTRON) {
            this.#disposableStack.defer(
                effect(() => {
                    const isIdle = pageVisibility.isIdle.value;
                    untracked(() => table.setThrottle(isIdle ? getIdleTableThrottle() : undefined, !isIdle));
                }),
            );
        }

        this.headerElements = makeHeaderElements<TableWidgetModel['eventBus']>([
            ...COMMON_HEADER_ELEMENTS,
            { type: 'scrollingTableIndicator', position: 20.5, alwaysVisible: true },
            { type: 'stickyFilter', position: 40, priority: 5 },
            // Collapses after the widget's own controls, before the channel picker and window controls (AC-FILTER-006).
            { type: 'columnFilters', position: 41, priority: 90 },
            { type: 'tableRowCount', position: 110, priority: 20 },
            { type: 'connectionStatus', position: 111, priority: 21 },
            { type: 'tableWidgetSettings', position: 112, priority: 22 },
        ]);
    }

    get type(): 'table' {
        return 'table';
    }

    get dirty(): ReadonlySignal<boolean> {
        return this.#model.dirty;
    }

    get columnTemplate(): ReadonlySignal<ColumnTemplate> {
        return this.#model.columnTemplate;
    }

    get columnFilterState(): ColumnFiltersModel {
        return this.#model.columnFilterState;
    }

    get columnFilters(): TableColumnFilters {
        return this.#model.columnFilters;
    }

    get classOptions(): TableWidgetModel['classOptions'] {
        return this.#model.classOptions;
    }

    get eventBus(): TableWidgetModel['eventBus'] {
        return this.#model.eventBus;
    }

    get table(): TableModel {
        return this.#table;
    }

    get filteredTableKeyOptions(): TableWidgetModel['filteredTableKeyOptions'] {
        return this.#model.filteredTableKeyOptions;
    }

    get resolvedColumns(): ReadonlySignal<OutputColumnWithMetadata[]> {
        return this.#model.resolvedColumns;
    }

    get tableSettingsOpen(): ReadonlySignal<boolean> {
        return this.#model.tableSettingsOpen;
    }

    get tableKeyResultSummary(): ReadonlySignal<string> {
        return this.#model.tableKeyResultSummary;
    }

    get searchQuery(): ReadonlySignal<string> {
        return this.#model.searchQuery;
    }

    get selectedTableKey(): ReadonlySignal<string | null> {
        return this.#model.selectedTableKey;
    }

    get selectedRowIds(): ReadonlySignal<ReadonlySet<GridRowId>> {
        return this.#model.selectedRowIds;
    }

    get state(): ReadonlySignal<TableWidgetRuntimeState> {
        return this.#model.state;
    }

    get symbolIdField(): ReadonlySignal<string | undefined> {
        return this.#model.symbolIdField;
    }

    get tableClass(): ReadonlySignal<TableClass | null> {
        return this.#model.tableClass;
    }

    get tableKeyOptions(): TableWidgetModel['tableKeyOptions'] {
        return this.#model.tableKeyOptions;
    }

    selectTableKey(value: string): void {
        this.#model.selectTableKey(value);
    }

    setTableSettingsOpen(open: boolean): void {
        this.#model.setTableSettingsOpen(open);
    }

    setSearchQuery(query: string): void {
        this.#model.setSearchQuery(query);
    }

    setTableClass(tableClass: TableClass | null): void {
        this.#model.setTableClass(tableClass);
    }

    updateRowSelection(change: GridRowSelectionChange): void {
        this.#model.updateRowSelection(change);
    }

    get viewData(): ReadonlySignal<TableWidgetViewData | null> {
        return this.#model.viewData;
    }

    updateColumnTemplate(columnTemplate: ColumnTemplate): void {
        this.#model.updateColumnTemplate(columnTemplate);
    }

    toJSON(): TableWidgetState {
        return this.#model.toJSON();
    }

    markClean(): void {
        this.#model.markClean();
    }

    [Symbol.dispose](): void {
        this.#disposableStack.dispose();
    }
}
